import { Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"

import { ArrowDownward, ArrowUpward } from "@mui/icons-material"
import chroma from "chroma-js"
import _find from "lodash/find"
import _maxBy from "lodash/maxBy"
import _orderBy from "lodash/orderBy"
import React from "react"
import styled from "styled-components"

type TValueFormatter = (value: number) => string

type TBenchmarkChartDatum = { base?: boolean; value: number; label: string }

interface IProps {
  /**
   * The bars data
   * @default []
   */
  data: TBenchmarkChartDatum[]
  /**
   * Whether to flip the red/green colors. By default higher = red, lower = green. i.e. it's a good for others to be higher.
   * @default false
   */
  invertColors: boolean
  /**
   * Key text to use for higher / lower key
   * @default ["Higher", "Lower"]
   */
  keyText: string[]
  /**
   * The maximum distance away from the base price. This can be used to scale the bars. By default, 50% of the maximum value in the data will be used, meaning the largest bar will occupt 25% of the component width.
   * @default 0
   */
  maxDifference: number
  /**
   * Additional styles to be added to the wrapper for layout purposes
   * @default {}
   */
  styles: React.CSSProperties
  /**
   * A subtitle to be displayed above the bars
   * @default ""
   */
  subtitle: string
  /**
   * Show the more expensive / less expensive key
   * @default true
   */
  showKey: boolean
  /**
   * Show the percentages above each line
   * @default true
   */
  showPercentages: boolean
  /**
   * A title to be displayed above the bars
   * @default ""
   */
  title: string
  /**
   * A function which will format the display of values
   * @default v => `${v}`
   */
  valueFormatter: TValueFormatter
}

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
  > header {
    margin-top: 1rem;
    text-align: center;
  }
`

const TitleWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

const BenchmarkWrap = styled.div`
  padding: 1rem 0;
  width: 100%;
`

const BaseRow = styled.div<{ color: string }>`
  position: relative;
  background: radial-gradient(
    200px,
    ${p => chroma(p.color).alpha(0.06).css()},
    transparent
  );
  border-top: 1px solid ${p => chroma(p.color).alpha(0.1).css()};
  border-bottom: 1px solid ${p => chroma(p.color).alpha(0.1).css()};
`
const HigherRow = styled.div`
  position: relative;
`
const LowerRow = styled.div`
  position: relative;
`
const Key = styled.div`
  position: absolute;
  text-align: center;
  opacity: 0.5;
  text-transform: uppercase;
  max-width: 90px;

  > svg {
    margin: 0 auto;
  }

  > p {
    font-size: 0.8rem;
  }
`

const HigherKey = styled(Key)`
  left: 1rem;
  bottom: 0.5rem;
`

const LowerKey = styled(Key)`
  right: 1rem;
  top: 0.5rem;
`

const LineWrap = styled.div<{ color: string }>`
  margin: 0.5rem 0;
  padding: 0;
  width: 100%;
  display: grid;
  grid-template-columns: 0.5fr 1px 0.5fr;
  position: relative;

  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;

    > span {
      position: absolute;

      > i {
        color: ${p => p.color};
      }
    }
  }
`

const LineWrapLeft = styled.div`
  justify-content: flex-end;
`
const LineWrapMiddle = styled.div`
  justify-content: center;
`
const LineWrapRight = styled.div`
  justify-content: flex-start;
`

const MidLine = styled.div<{ backgroundColor: string; borderColor: string }>`
  width: 1px;
  margin: 0;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.6rem;
  background: rgba(255, 255, 255, 0.5);
  position: relative;
  z-index: 1;

  &::after {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    left: -3.5px;
    position: absolute;
    border: 1px solid ${p => p.borderColor};
    background: ${p => p.backgroundColor};
  }
`

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  border: 1px solid transparent;
`
const Line = styled.div<{
  cap: "start" | "end" | ""
  diff: string | boolean | null
  length: number
  colors: string[]
}>`
  height: 0px;
  width: ${p => p.length}%;
  border-width: 1px 0 0 0;
  border-style: ${p => (!!p.diff ? "solid" : "none")};
  color: ${(p) => {
    return p.cap === "end" ? p.colors[0] : p.colors[1]
  }};
  border-color: currentColor;
  position: relative;

  ${p =>
    p.diff
      ? `&::before {
    content: "${p.diff}";
    width: 100%;
    text-align: center;
    height: 20px;
    position: absolute;
    left: 0;
    display: block;
    top: -1rem;
    color: currentColor;
    font-size: 0.8rem;
    margin-top: -4px;
  }`
      : ""}

  &::after {
    content: "";
    background: ${(p) => {
      return p.cap === "end" ? p.colors[0] : p.colors[1]
    }};
    width: 8px;
    height: 8px;
    border-radius: 50%;
    position: absolute;
    display: ${p => (!!p.length ? "block" : "none")};
    top: -4.5px;
    left: ${p => (p.cap === "start" ? "-4px" : "auto")};
    right: ${p => (p.cap === "end" ? "-4px" : "auto")};
  }
`

interface IBenchmarkLineProps {
  key?: string
  baseValue: number
  formattedValue: string
  invertColors?: boolean
  isBase?: boolean
  label: string
  maxDifference: number
  position: string
  value: number
  showPercentages: boolean
}

const getFormattedDifference = ({
  baseValue,
  value
}: {
  baseValue: number
  value: number
}) => {
  if (baseValue === value) {
    return ""
  } else if (baseValue < value) {
    return `+${Math.round((100 * (value - baseValue)) / baseValue)}%`
  } else {
    return `${Math.round((100 * (value - baseValue)) / baseValue)}%`
  }
}

const BenchmarkLineWrap = ({
  baseValue,
  formattedValue,
  invertColors = false,
  isBase = false,
  label,
  maxDifference,
  position,
  showPercentages = true,
  value
}: IBenchmarkLineProps) => {
  const theme = useTheme()
  const colorScales = theme.colorScales
  const lineLength = (100 * Math.abs(value - baseValue)) / maxDifference
  const formattedDifference = isBase
    ? null
    : getFormattedDifference({ baseValue, value })

  const lineColorsArr = invertColors
    ? [colorScales.reds(1).css(), colorScales.greens(1).css()]
    : [colorScales.greens(1).css(), colorScales.reds(1).css()]

  return (
    <LineWrap color={isBase ? theme.basePalette.fg : theme.basePalette.fgAlpha}>
      <LineWrapLeft>
        {position === "below" ? (
          <>
            {!isNaN(lineLength) ? (
              <Line
                length={lineLength}
                cap="start"
                diff={showPercentages && formattedDifference}
                colors={lineColorsArr}
              />
            ) : (
              ""
            )}
            <span style={{ right: `${lineLength + 2}%`, padding: "0 0.5rem" }}>
              <i style={{ padding: "0 2px" }}>{label}</i> {formattedValue}
            </span>
          </>
        ) : (
          ""
        )}
      </LineWrapLeft>
      <LineWrapMiddle>
        <MidLine
          backgroundColor={
            isBase ? theme.basePalette.bg : colorScales.light(0).css()
          }
          borderColor={isBase ? colorScales.light(0).css() : "transparent"}
        />
      </LineWrapMiddle>
      <LineWrapRight>
        {position === "above" || position === "equal" ? (
          <>
            {!isNaN(lineLength) ? (
              <Line
                colors={lineColorsArr}
                length={lineLength}
                cap="end"
                diff={showPercentages && formattedDifference}
              />
            ) : (
              <Dot />
            )}
            <span style={{ left: `${lineLength + 2}%`, padding: "0 0.5rem" }}>
              {formattedValue} <i style={{ padding: "0 2px" }}>{label}</i>
            </span>
          </>
        ) : (
          ""
        )}
      </LineWrapRight>
    </LineWrap>
  )
}

export const Benchmark = ({
  data = [],
  invertColors = false,
  keyText = ["Higher", "Lower"],
  maxDifference = 0,
  styles = {},
  subtitle = "",
  title = "",
  showKey = true,
  showPercentages = true,
  valueFormatter = v => `${v}`
}: IProps) => {
  const theme = useTheme()
  if (!data || !data.length) {
    return <p>No Data</p>
  }

  const base: TBenchmarkChartDatum | undefined = _find(data, { base: true })
  if (typeof base === "undefined" || !base || !base.value) {
    return <div />
  }
  const aboveBenchmark: TBenchmarkChartDatum[] = _orderBy(
    data.filter((d: TBenchmarkChartDatum) => d.value > base.value && !d.base),
    "value",
    "desc"
  )
  const belowBenchmark: TBenchmarkChartDatum[] = _orderBy(
    data.filter((d: TBenchmarkChartDatum) => d.value < base.value && !d.base),
    "value",
    "desc"
  )
  const equalToBenchmark: TBenchmarkChartDatum[] = data.filter(
    (d: TBenchmarkChartDatum) => d.value === base.value && !d.base
  )
  const maxElm = _maxBy(data, d => Math.abs(d.value - base.value))
  if (typeof maxElm === "undefined") {
    return "Could not find max element in data"
  }
  const derivedMaxDifference = Math.abs(maxElm.value - base.value) * 2

  return (
    <Wrapper style={{ ...styles }}>
      <header>
        {title ? (
          <TitleWrap>
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
            {subtitle ? <Typography gutterBottom>{subtitle}</Typography> : ""}
          </TitleWrap>
        ) : (
          ""
        )}
      </header>
      <BenchmarkWrap>
        {aboveBenchmark && aboveBenchmark.length ? (
          <HigherRow>
            {aboveBenchmark.map((d: TBenchmarkChartDatum) => (
              <BenchmarkLineWrap
                key={d.label}
                invertColors={invertColors}
                position="above"
                label={d.label}
                value={d.value}
                baseValue={base.value}
                maxDifference={Math.max(derivedMaxDifference, maxDifference)}
                formattedValue={valueFormatter(d.value)}
                showPercentages={showPercentages}
              />
            ))}
            {showKey && keyText && keyText.length === 2 ? (
              <HigherKey>
                <ArrowUpward />
                <Typography>{keyText[0]}</Typography>
              </HigherKey>
            ) : (
              ""
            )}
          </HigherRow>
        ) : (
          ""
        )}
        <BaseRow color={theme.basePalette.fg}>
          <BenchmarkLineWrap
            isBase={true}
            position="equal"
            label={base.label}
            value={base.value}
            baseValue={base.value}
            maxDifference={Math.max(derivedMaxDifference, maxDifference)}
            formattedValue={valueFormatter(base.value)}
            showPercentages={showPercentages}
          />
        </BaseRow>
        {equalToBenchmark && equalToBenchmark.length ? (
          <div>
            {equalToBenchmark.map((d: TBenchmarkChartDatum) => (
              <BenchmarkLineWrap
                key={d.label}
                invertColors={invertColors}
                position="equal"
                label={d.label}
                value={d.value}
                baseValue={base.value}
                maxDifference={Math.max(derivedMaxDifference, maxDifference)}
                formattedValue={valueFormatter(d.value)}
                showPercentages={showPercentages}
              />
            ))}
          </div>
        ) : (
          ""
        )}
        {belowBenchmark && belowBenchmark.length ? (
          <LowerRow>
            {belowBenchmark.map((d: TBenchmarkChartDatum) => (
              <BenchmarkLineWrap
                key={d.label}
                invertColors={invertColors}
                position="below"
                label={d.label}
                value={d.value}
                baseValue={base.value}
                maxDifference={Math.max(derivedMaxDifference, maxDifference)}
                formattedValue={valueFormatter(d.value)}
                showPercentages={showPercentages}
              />
            ))}
            {showKey && keyText && keyText.length === 2 ? (
              <LowerKey>
                <Typography>{keyText[1]}</Typography>
                <ArrowDownward />
              </LowerKey>
            ) : (
              ""
            )}
          </LowerRow>
        ) : (
          ""
        )}
      </BenchmarkWrap>
    </Wrapper>
  )
}

export default Benchmark
